import { FC } from 'react';
import { Box, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import Icon from '@/components/icon';
import Text from '@/components/text';
import Card from '@/components/card';
import { CLASSIFIER_OPTIONS, REGEX_PATTERN_TEMPLATES } from './helpers';
import { ClassifierCreateOption } from '@/lib/models/classifier';
import { ReadonlySection } from '@/components/form/readonly-view/section';
import classNames from 'classnames';

interface Props {
  onSelect: (type: ClassifierCreateOption) => void;
  onSelectRegexTemplate: (templateId: string) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  cardButton: {
    alignItems: 'center',
    textAlign: 'center',
  },
  typeButton: {
    minHeight: 140,
    width: 280,
    justifyContent: 'center',
    margin: 24,
    padding: 24,
  },
  exampleButton: {
    width: 250,
    height: 180,
    textAlign: 'left',
  },
  templatePattern: {
    fontFamily: 'monospace',
    fontSize: theme.typography.fontSize,
    marginTop: 8,
  },
}));

const ClassifierTypeSelector: FC<Props> = ({ onSelect, onSelectRegexTemplate }) => {
  const styles = useStyles();

  const compTypeCard = classNames(styles.typeButton, styles.cardButton);
  const compExampleButton = classNames(styles.exampleButton, styles.cardButton);

  return (
    <Stack>
      <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={2}>
        {CLASSIFIER_OPTIONS.map(({ type, icon, title, description }) => (
          <Card key={type} className={compTypeCard} onClick={() => onSelect(type)} hover>
            <Box mb={2}>
              <Icon name={icon} size="large" color="link" />
            </Box>
            <Text bold>{title}</Text>
            <Text size="small" color="grey">
              {description}
            </Text>
          </Card>
        ))}
      </Stack>

      <ReadonlySection label="Pattern templates" />
      <Stack mt={2} gap={2}>
        <Text size="small" color="grey">
          Start a custom entity with common enterprise examples. Fields and sample test text are
          prefilled; you can edit everything before saving.
        </Text>
        <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={2}>
          {REGEX_PATTERN_TEMPLATES.map((template) => {
            const { id, icon, title, summary, regexPattern } = template;
            return (
              <Card
                key={id}
                className={compExampleButton}
                onClick={() => onSelectRegexTemplate(id)}
                hover
              >
                <Stack direction="row" alignItems="center" gap={1} marginBottom={1}>
                  <Icon name={icon} size="small" color="link" />
                  <Text bold>{title}</Text>
                </Stack>
                <Text size="small" color="grey">
                  {summary}
                </Text>
                <Text className={styles.templatePattern}>{regexPattern}</Text>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ClassifierTypeSelector;
